"use client";

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth-client";

/**
 * Clears the TanStack Query cache whenever the authenticated user changes
 * (sign-out, sign-in, or switching accounts) so a new user's screens can
 * never render a previous user's cached queries. Client-side auth
 * transitions (e.g. the sign-in form's `router.push`) don't remount
 * `QueryProvider`, so the cache would otherwise survive across users on a
 * shared device. See issue #76.
 */
export function QueryCacheAuthSync() {
  const queryClient = useQueryClient();
  const { data: session, isPending } = authClient.useSession();
  // undefined = not yet initialized (don't clear on the very first resolve).
  const lastUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (isPending) return;
    const currentUserId = session?.user?.id ?? null;
    if (
      lastUserId.current !== undefined &&
      lastUserId.current !== currentUserId
    ) {
      queryClient.clear();
    }
    lastUserId.current = currentUserId;
  }, [isPending, session?.user?.id, queryClient]);

  return null;
}
